import type { LocalPhoto } from '@/features/calibrationPhotos';
import type { CalibrationRuntimeData } from '@/features/calibration/hooks/useCalibrationRuntime/types';
import type { PromotedPhoto } from '../types';

type PhotoWithDefaults = NonNullable<
  CalibrationRuntimeData['photos']
>[number] & {
  isCoverPhoto: boolean;
  isPromotedToWorkOrder: boolean;
  caption: string;
};

const ensurePhotoDefaults = (
  photos: CalibrationRuntimeData['photos']
): PhotoWithDefaults[] => {
  if (!photos) return [];
  return photos.map(photo => ({
    isCoverPhoto: false,
    isPromotedToWorkOrder: false,
    caption: '',
    ...photo
  }));
};

export type CoverPhotoData = {
  url: string | null;
  index: number | null;
};

export const getCoverPhotoData = (
  calibrationData: CalibrationRuntimeData | null
): CoverPhotoData => {
  if (!calibrationData?.photos || calibrationData.photos.length === 0) {
    return { url: null, index: null };
  }

  const photos = ensurePhotoDefaults(calibrationData.photos);
  const coverPhotoIndex = photos.findIndex(p => p.isCoverPhoto);

  if (coverPhotoIndex !== -1) {
    return {
      url: photos[coverPhotoIndex].thumbnailDataUrl || null,
      index: coverPhotoIndex
    };
  }

  return {
    url: photos[0].thumbnailDataUrl || null,
    index: 0
  };
};

export type PromotedPhotosData = {
  photos: PromotedPhoto[];
  indices: number[];
};

export const getPromotedPhotosData = (
  calibrationData: CalibrationRuntimeData | null,
  markAsUnsynced = false
): PromotedPhotosData => {
  if (!calibrationData?.photos || calibrationData.photos.length === 0) {
    return { photos: [], indices: [] };
  }

  const photos = ensurePhotoDefaults(calibrationData.photos);
  const promotedIndices: number[] = [];
  const promotedPhotos: PromotedPhoto[] = [];

  photos.forEach((p, index) => {
    if (p.isPromotedToWorkOrder) {
      promotedIndices.push(index);
      promotedPhotos.push({
        id: p.id,
        thumbnailDataUrl: p.thumbnailDataUrl,
        caption: p.caption || null,
        isUnsynced: markAsUnsynced || p.syncStatus === 'pending'
      });
    }
  });

  return { photos: promotedPhotos, indices: promotedIndices };
};

export const convertLocalPhotosToCalibrationPhotos = (
  localPhotos: LocalPhoto[]
): CalibrationRuntimeData['photos'] => {
  return localPhotos.map(lp => ({
    id: lp.id,
    filename: lp.filename,
    size: lp.size,
    mimeType: lp.mimeType,
    capturedAt: lp.capturedAt,
    thumbnailDataUrl: lp.thumbnailDataUrl,
    isCoverPhoto: lp.isCoverPhoto,
    isPromotedToWorkOrder: lp.isPromotedToWorkOrder,
    caption: lp.caption,
    syncStatus: 'pending' as const,
    retryCount: 0
  }));
};

export const hasUnsyncedLocalPhotos = (
  localPhotos: LocalPhoto[],
  serverPhotos: CalibrationRuntimeData['photos']
): boolean => {
  if (localPhotos.length === 0) return false;

  const syncedServerPhotos = (serverPhotos || []).filter(
    p => p.syncStatus === 'synced'
  );

  if (syncedServerPhotos.length > 0) {
    const syncedIds = new Set(syncedServerPhotos.map(p => p.id));
    return localPhotos.some(lp => !syncedIds.has(lp.id));
  }

  return true;
};
